'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import { Crown, Sparkles, Loader2, Gift, Calendar, MessageCircle } from 'lucide-react';
import { useAuth } from '@/app/user-and-session/session-auth/hooks/useAuth';
import { useUserAppData } from '@/app/user-and-session/user-app-data/useUserAppData';
import { getCustomerSubscriptionDetails, createCustomerPortalSession } from '@/app/subscription/billing/server/stripe';
import { CustomerSubscription } from '@/app/subscription/billing/server/data';
import { safePathFromCallback } from '@/app/subscription/billing/utils/callbackSecurity';
import { useTranslations } from 'next-intl';
import { 
  getCreditInfoActionSQL, 
  claimDiscordRewardActionSQL, 
  claimDailyRewardActionSQL
} from '@/app/subscription/user-credit/server/credit_info_action';
import { CreditInfo, CreditAward, PermanentCreditHistoryMessage } from '@/app/subscription/user-credit/credit';

interface UserBalanceProps {
  className?: string;
  callbackBaseUrl?: string;
}

export default function UserBalance({ className = '', callbackBaseUrl = '/profile' }: UserBalanceProps) {
  const { userEmail, isAuthenticated, isLoading: authLoading } = useAuth();
  const t = useTranslations('subscription');
  
  // User app data hook for gem count (singleton) - only after auth loading completes
  const [userAppDataState, userAppDataActions] = useUserAppData(
    !authLoading && isAuthenticated && userEmail ? userEmail : ''
  );
  const [customerSubscriptions, setCustomerSubscriptions] = useState<CustomerSubscription[]>([]);
  const [subscriptionDetailsLoading, setSubscriptionDetailsLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [creditInfo, setCreditInfo] = useState<CreditInfo | null>(null);
  const [creditInfoLoading, setCreditInfoLoading] = useState(true);
  const [claimingDiscord, setClaimingDiscord] = useState(false);
  const [claimingDaily, setClaimingDaily] = useState(false);

  // Function to get plan icon based on plan name
  const getPlanIcon = (planName: string) => {
    const planNameLower = planName.toLowerCase();
    if (planNameLower.includes('basic')) {
      return Sparkles;
    } else if (planNameLower.includes('premium') || planNameLower.includes('pro')) {
      return Crown;
    }
    return Sparkles; // default
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const safeCallbackUrl = safePathFromCallback(callbackBaseUrl);
      const result = await createCustomerPortalSession(safeCallbackUrl || undefined);
      
      if (!result.success) {
        console.error('Failed to create portal session:', result.error);
        alert(`Failed to access subscription management: ${result.error}`);
        return;
      }

      if (result.url) {
        window.open(result.url, '_blank');
      }
    } catch (error) {
      console.error('Error accessing subscription management:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleClaimDiscord = async () => {
    // Open Discord in new tab immediately
    window.open('https://discord.gg/Vn2JpusbXX', '_blank', 'noopener,noreferrer');

    if (!userEmail || claimingDiscord) return;
    
    setClaimingDiscord(true);
    try {
      const claimResult = await claimDiscordRewardActionSQL(userEmail);
      
      if (claimResult.success) {
        // Refresh credit info after claiming
        const result = await getCreditInfoActionSQL(userEmail);
        if (result.success) {
          setCreditInfo(result.creditInfo);
        }
        
        // Refresh user app data to update gem count
        await userAppDataActions.initializeUserDataFromDB(userEmail);
      } else {
        alert(claimResult.message || 'Failed to claim discord reward.');
      }
    } catch (error) {
      console.error('Error claiming discord reward:', error);
      alert('Failed to claim discord reward. Please try again.');
    } finally {
      setClaimingDiscord(false);
    }
  };

  const handleClaimDaily = async () => {
    if (!userEmail || claimingDaily) return;

    setClaimingDaily(true);
    try {
      const claimResult = await claimDailyRewardActionSQL(userEmail);

      if (claimResult.success) {
        // Refresh credit info after claiming
        const result = await getCreditInfoActionSQL(userEmail);
        if (result.success) {
          setCreditInfo(result.creditInfo);
        }

        // Refresh user app data to update gem count
        await userAppDataActions.initializeUserDataFromDB(userEmail);
      } else {
        alert(claimResult.message || 'Failed to claim daily reward.');
      }
    } catch (error) {
      console.error('Error claiming daily reward:', error);
      alert('Failed to claim daily reward. Please try again.');
    } finally {
      setClaimingDaily(false);
    }
  };

  useEffect(() => {
    const fetchCustomerSubscriptionDetails = async () => {
      try {
        const result = await getCustomerSubscriptionDetails();
        if (result.success) {
          setCustomerSubscriptions(result.subscriptions || []);
        } else {
          console.error('Failed to fetch customer subscription details:', result.error);
        }
      } catch (error) {
        console.error('Error fetching customer subscription details:', error);
      } finally {
        setSubscriptionDetailsLoading(false);
      }
    };

    const fetchCreditInfo = async () => {
      if (!userEmail) return;

      try {
        const result = await getCreditInfoActionSQL(userEmail);
        if (result.success) {
          setCreditInfo(result.creditInfo);
        } else {
          console.error('Failed to fetch credit info:', result.message);
        }
      } catch (error) {
        console.error('Error fetching credit info:', error);
      } finally {
        setCreditInfoLoading(false);
      }
    };

    // fetchCustomerSubscriptionDetails();
    fetchCreditInfo();

    return () => {
      if (typeof window !== 'undefined') {
        delete (window as any).refreshSubscriptionCredits;
      }
    };
  }, [userEmail]);

  // Get active subscriptions
  const activeSubscriptions = customerSubscriptions.filter(subscription => subscription.is_active);

  // Create combined history list with daily reward if applicable
  const getCombinedHistory = (): (PermanentCreditHistoryMessage & { expires?: string })[] => {
    if (!creditInfo) return [];
    
    // Debug log to see the raw data
    console.log('Raw credit history data:', creditInfo.credit_history);
    
    // Parse credit history - handle both object and string formats
    let parsedHistory: PermanentCreditHistoryMessage[] = [];
    try {
      if (Array.isArray(creditInfo.credit_history)) {
        parsedHistory = creditInfo.credit_history.map(item => {
          // If item is a string, parse it
          if (typeof item === 'string') {
            const parsed = JSON.parse(item);
            console.log('Parsed item:', parsed);
            return parsed;
          }
          console.log('Direct item:', item);
          return item;
        });
      }
    } catch (error) {
      console.error('Error parsing credit history:', error);
      parsedHistory = [];
    }
    
    const history = [...parsedHistory.map(item => ({ ...item, expires: '' }))];
    
    // Add daily reward entry if received
    if (creditInfo.has_received_daily) {
      const today = new Date().toISOString();
      history.push({
        name: 'Daily',
        date: today,
        amount: CreditAward.daily,
        expires: 'today'
      });
    }
    
    // Sort by date in descending order (newest first)
    history.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateB - dateA; // Descending order
    });
    
    return history;
  };

  // Helper function to safely format date
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return date.toLocaleDateString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  return (
    <div className={`bg-gradient-to-br from-[#1a1a1e] to-[#2a2a2e] rounded-2xl p-6 md:p-8 border border-white/5 ${className}`}>
      <h2 className="text-2xl font-bold text-white mb-6">{t('myBalance')}</h2>

      {/* Gem Balance Container */}
      <div className="bg-[#25252a] rounded-3xl p-6 mb-8 border border-white/10">
        <div className="flex items-center gap-4">
          <div>
            {authLoading || !userAppDataState.isInitialized ? (
              <Loader2 className="w-6 h-6 animate-spin text-white" />
            ) : (
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-white text-3xl font-bold">{userAppDataState.creditCount + userAppDataState.dailyCreditCount}</p>
                  <Image
                    src="/credit_icons/gem.png"
                    alt="Gem"
                    width={20}
                    height={20}
                    className="w-6 h-6"
                  />
                </div>
                {/* <p className="text-gray-400 text-sm mt-1">
                  Daily Gems Left: {userAppDataState.dailyCreditCount || 0}
                </p> */}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Claim Rewards Heading */}
      <h3 className="text-lg font-semibold text-white mb-4">{t('claimRewards')}</h3>

      {/* Reward Section Container */}
      <div className="flex flex-col gap-4 mb-8">
          {creditInfoLoading ? (
            <>
              <div className="rounded-xl p-4 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            </>
          ) : (
            <>
              {/* Daily Reward Card */}
              {/* <div className="bg-[#2a2a2e] rounded-xl p-4 border border-white/5 hover:border-white/10 transition-all flex-1">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="mb-2">
                      <span className="text-lg font-bold text-white">Daily Reward</span>
                    </div>
                    <div className="text-xs text-gray-400">
                      Daily reward gems will reset at the end of each day.
                    </div>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-bold text-white">+{CreditAward.daily}</span>
                      <Image
                        src="/credit_icons/gem.png"
                        alt="Gem"
                        width={20}
                        height={20}
                        className="w-5 h-5"
                      />
                    </div>
                    <button
                      onClick={handleClaimDaily}
                      disabled={creditInfo?.has_received_daily || claimingDaily}
                      className="bg-gradient-to-r from-[#4f46e5] to-[#7c3aed] hover:from-[#5856d6] hover:to-[#8b5cf6] disabled:from-[#374151] disabled:to-[#374151] text-white py-2 px-6 rounded-xl text-sm font-medium transition-all disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {claimingDaily ? (
                        <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                      ) : creditInfo?.has_received_daily ? (
                        'Claimed'
                      ) : (
                        'Claim'
                      )}
                    </button>
                  </div>
                </div>
              </div> */}

              {/* Discord Reward Card */}
              <div className="bg-[#2a2a2e] rounded-xl p-4 border border-white/5 hover:border-white/10 transition-all flex-1">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-white">{t('joinDiscord')}</span>
                        <MessageCircle className="w-5 h-5 text-white" />
                      </div>
                    </div>
                    <div className="text-xs text-gray-400">
                      Join our <a
                        href="https://discord.gg/Vn2JpusbXX"
                        target="_blank"
                        rel="noopener,noreferrer"
                        className="underline hover:text-gray-300"
                      >
                        Discord community
                      </a> for latest updates.
                    </div>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-bold text-white">+{CreditAward.discord}</span>
                      <Image
                        src="/credit_icons/gem.png"
                        alt="Gem"
                        width={20}
                        height={20}
                        className="w-5 h-5"
                      />
                    </div>
                    <button
                      onClick={handleClaimDiscord}
                      disabled={creditInfo?.has_received_discord || claimingDiscord}
                      className="bg-gradient-to-r from-[#4f46e5] to-[#7c3aed] hover:from-[#5856d6] hover:to-[#8b5cf6] disabled:from-[#374151] disabled:to-[#374151] text-white py-2 px-6 rounded-xl text-sm font-medium transition-all disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {claimingDiscord ? (
                        <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                      ) : creditInfo?.has_received_discord ? (
                        t('claimed')
                      ) : (
                        t('claim')
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
      </div>

      {/* One-time Purchase Section */}
      <div className="mt-8">
        <h3 className="text-xl font-bold text-white mb-1">{t('buyGems')}</h3>
        <div className="text-sm text-gray-200 mb-4 mt-4 text-center">{t('gemsDescription')}</div>
      </div>

      {/* Subscription Status */}
      {subscriptionDetailsLoading ? (
        <div className="flex items-center justify-center py-8">
          {/* <p className="text-gray-400 text-sm">Checking subscription status...</p> */}
        </div>
      ) : activeSubscriptions.length > 0 ? (
        <div className="space-y-4">
          <h3 className="text-2xl font-bold text-white mb-6">{t('currentSubscription')}</h3>
          <div className="space-y-4">
            {activeSubscriptions.map((subscription) => (
              <div key={subscription.id} className="bg-[#2a2a2e] rounded-xl p-6 border border-white/5">
                <div className="mb-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-1">
                        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#4f46e5] to-[#7c3aed] hover:from-[#5856d6] hover:to-[#8b5cf6] text-white px-4 py-2 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl">
                          {(() => {
                            const IconComponent = getPlanIcon(subscription.plan.name);
                            return <IconComponent className="w-5 h-5" />;
                          })()}
                          <span className="text-lg font-bold">{subscription.plan.name}</span>
                        </div>
                      </div>
                      <p className="text-gray-400">{subscription.plan.description}</p>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="text-left">
                    <p className="text-white-400 text-sm">
                      {subscription.cancel_at_period_end ? (
                        <span className="text-white-400 font-normal">{t('cancelled')} </span>
                      ) : (
                        <span className="text-white-400 font-normal">${(subscription.plan.price / 100).toFixed(2)} billed monthly. </span>
                      )}
                      {subscription.cancel_at_period_end ? t('nextBillingDate').toLowerCase() : t('nextBillingDate')}{' '}
                      <span className="font-medium">
                        {subscription.next_billing_date ?
                          (() => {
                            try {
                              const date = new Date(subscription.next_billing_date * 1000);
                              return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString();
                            } catch (error) {
                              return 'Invalid Date';
                            }
                          })() :
                          'Unknown'
                        }
                      </span>
                    </p>
                  </div>
                </div>

                <div className="text-left">
                  {subscription.cancel_at_period_end ? (
                    <button
                      onClick={handleManageSubscription}
                      disabled={portalLoading}
                      className="text-white underline hover:no-underline disabled:text-gray-500 text-sm font-medium transition-all disabled:cursor-not-allowed"
                    >
                      {portalLoading ? 'Loading...' : t('continueSubscription')}
                    </button>
                  ) : (
                    <button
                      onClick={handleManageSubscription}
                      disabled={portalLoading}
                      className="text-white underline hover:no-underline disabled:text-gray-500 text-sm font-medium transition-all disabled:cursor-not-allowed"
                    >
                      {portalLoading ? 'Loading...' : t('manage')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Credit History */}
      <div className="mt-8">
        <h3 className="text-xl font-bold text-white mb-4">{t('history')}</h3>
        {creditInfoLoading ? (
          <div className="space-y-3">
            <div className="py-2">
              <div className="flex justify-between items-center">
                <div className="w-20 h-4 bg-gray-600 rounded animate-pulse"></div>
                <div className="w-16 h-4 bg-gray-600 rounded animate-pulse"></div>
                <div className="w-12 h-4 bg-gray-600 rounded animate-pulse"></div>
                <div className="w-14 h-4 bg-gray-600 rounded animate-pulse"></div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Header Row */}
            <div className="grid grid-cols-4 gap-4 px-4 py-2 text-xs font-medium text-gray-400 uppercase tracking-wider">
              <div>{t('date')}</div>
              <div>{t('type')}</div>
              <div>{t('amount')}</div>
              <div>{t('expires')}</div>
            </div>

            {/* History Entries */}
            {getCombinedHistory().length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-gray-400">{t('noCreditHistory')}</p>
              </div>
            ) : (
              getCombinedHistory().map((entry, index) => (
                <div key={index} className="py-2">
                  <div className="grid grid-cols-4 gap-4 items-center">
                    <div className="text-sm text-gray-400">
                      {formatDate(entry.date)}
                    </div>
                    <div className="text-sm text-white">
                      {entry.name}
                    </div>
                    <div className="text-sm font-medium text-white">
                      {entry.amount >= 0 ? '+' : ''}{entry.amount}
                    </div>
                    <div className="text-sm text-white">
                      {entry.expires === 'today' ? t('today') : (entry.expires || '—')}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
